import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface TopBarProps {
  activeAttacks?: number;
  activeRules?: number;
  protectedPrefixes?: number;
  avgMitigationTime?: string;
  onChangePasswordClick?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  activeAttacks = 0,
  activeRules = 0,
  protectedPrefixes = 0,
  avgMitigationTime = '0s',
  onChangePasswordClick
}) => {
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const metrics = [
    {
      icon: '🔴',
      label: 'Ataques Ativos',
      value: activeAttacks,
      color: activeAttacks > 0 ? 'var(--color-critical)' : 'var(--color-success)',
      bgColor: activeAttacks > 0 ? 'rgba(220, 38, 38, 0.1)' : 'rgba(16, 185, 129, 0.1)'
    },
    {
      icon: '🛡️',
      label: 'Regras Ativas',
      value: activeRules,
      color: 'var(--color-primary)',
      bgColor: 'rgba(14, 165, 233, 0.1)'
    },
    {
      icon: '🌐',
      label: 'Prefixos Protegidos',
      value: protectedPrefixes,
      color: 'var(--color-success)',
      bgColor: 'rgba(16, 185, 129, 0.1)'
    },
    {
      icon: '⚡',
      label: 'Tempo Médio Mitigação',
      value: avgMitigationTime,
      color: 'var(--color-warning)',
      bgColor: 'rgba(245, 158, 11, 0.1)'
    }
  ];

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: '240px',
      right: 0,
      height: '60px',
      background: 'var(--color-bg-secondary)',
      borderBottom: '1px solid var(--color-border)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 var(--spacing-6)',
      gap: 'var(--spacing-6)',
      zIndex: 999
    }}>
      {/* Logo/Title for small screens */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-3)'
      }}>
        <h1 style={{
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--color-text-primary)',
          margin: 0
        }}>
          SOC CORERO Dashboard
        </h1>
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Persistent Metrics */}
      <div style={{
        display: 'flex',
        gap: 'var(--spacing-4)',
        alignItems: 'center'
      }}>
        {metrics.map((metric, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: metric.bgColor,
              border: `1px solid ${metric.color}20`,
              borderRadius: 'var(--radius-lg)',
              minWidth: '140px'
            }}
          >
            <span style={{ fontSize: 'var(--text-lg)' }}>{metric.icon}</span>
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-tertiary)',
                marginBottom: '0.125rem',
                whiteSpace: 'nowrap'
              }}>
                {metric.label}
              </div>
              <div style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-bold)',
                color: metric.color
              }}>
                {metric.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Current Time */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-2)',
        padding: 'var(--spacing-2) var(--spacing-3)',
        background: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <span style={{ fontSize: 'var(--text-base)' }}>🕐</span>
        <div style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--color-text-secondary)'
        }}>
          {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {/* User Menu */}
      {user && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-bg-elevated)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              cursor: 'pointer',
              color: 'var(--color-text-primary)'
            }}
          >
            <span style={{ fontSize: 'var(--text-base)' }}>👤</span>
            <div style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-text-secondary)'
            }}>
              {user.name}
            </div>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
              {showUserMenu ? '▲' : '▼'}
            </span>
          </button>

          {showUserMenu && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              minWidth: '200px',
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              zIndex: 1000
            }}>
              <div style={{
                padding: 'var(--spacing-3)',
                borderBottom: '1px solid var(--color-border)'
              }}>
                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
                  {user.name}
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                  {user.role === 'admin' ? 'Administrador' : user.role === 'operator' ? 'Operador' : 'Usuário'}
                </div>
              </div>

              <div style={{ padding: 'var(--spacing-2)' }}>
                {onChangePasswordClick && (
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onChangePasswordClick();
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      color: 'var(--color-text-secondary)',
                      fontSize: 'var(--text-sm)',
                      textAlign: 'left',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-elevated)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span>🔐</span>
                    Alterar Senha
                  </button>
                )}

                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    logout();
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    color: 'var(--color-critical)',
                    fontSize: 'var(--text-sm)',
                    textAlign: 'left',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span>🚪</span>
                  Sair
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
